import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  TrendDown,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Calculator,
  Scales,
  ChartBar,
  Percent,
  Brain,
  ArrowsClockwise,
  Smiley,
  SmileyMeh,
  Heart,
  Heartbeat,
  XCircle,
  Trophy,
  Crosshair,
  BookOpen
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const WinRateTrading = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'What Is Win Rate in Trading | And Why It Can Mislead You',
      description: 'Learn what win rate is in trading and why it can be misleading if not combined with other performance metrics.',
      path: '/win-rate-trading',
      lang: 'en',
      alternates: getArticleAlternates('/win-rate-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Is a high win rate always good?",
      answer: "No. A high win rate means nothing without context. A trader who wins 80% of the time but loses $500 on each loss while only making $100 on each win is losing money overall. The size of wins versus losses matters just as much—often more—than the frequency of winning. A 90% win rate with poor risk/reward can be far worse than a 40% win rate with excellent risk/reward."
    },
    {
      question: "Can you be profitable with a low win rate?",
      answer: "Absolutely. Many successful trading strategies operate with win rates below 50%. Trend-following strategies, for example, often have 30-40% win rates but are highly profitable because when they win, they win big. The math is simple: if you win 35% of the time but your average winner is 4x your average loser, you're solidly profitable."
    },
    {
      question: "What is more important than win rate?",
      answer: "Profit factor (total profits ÷ total losses) gives a clearer picture of strategy quality. Expectancy (average profit per trade) tells you actual profitability. Risk-adjusted returns show how much risk you take for your gains. Consistency across time reveals sustainability. Win rate is one piece; these metrics together tell the whole story."
    },
    {
      question: "How does win rate affect profitability?",
      answer: "Win rate is one component of the profitability equation: Expectancy = (Win Rate × Average Win) - (Loss Rate × Average Loss). Higher win rate allows for lower risk/reward ratios while remaining profitable. Lower win rate requires higher risk/reward ratios. The relationship is mathematical—you can find profitable combinations at any win rate level if the other variables align."
    },
    {
      question: "What win rate do professional traders have?",
      answer: "It varies dramatically by strategy. Day traders might target 50-60%. Swing traders often operate at 40-55%. Trend followers can be profitable at 30-40%. There's no 'professional' win rate—what matters is that win rate, combined with risk/reward, produces positive expectancy. Many highly successful traders have lower win rates than amateurs because they prioritize letting winners run."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Percent size={16} weight="fill" />
            <span>Trading Metrics</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            What Is Win Rate in Trading
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              (And Why It Can Be Misleading)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            "I have a 75% win rate!" It sounds impressive. It feels like success. But here's the 
            uncomfortable truth: <strong className="text-white">win rate alone tells you almost 
            nothing about profitability</strong>. A trader with a 75% win rate can be losing money, 
            while someone with a 35% win rate can be highly profitable.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            Most traders obsess over <strong className="text-white">trading win rate</strong> because 
            it's simple to understand and psychologically satisfying. Winning feels good. Winning 
            often feels even better. But this focus on frequency of winning—rather than magnitude 
            of winning—creates dangerous blind spots.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Understanding <strong className="text-white">what is win rate</strong> in proper context 
            and why it can mislead you is essential to evaluating your actual performance—not just 
            how you feel about it.
          </p>
        </section>

        {/* Section 1: What Is Win Rate */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            What Is Win Rate (Simple Explanation)
          </h2>
          
          {/* Formula Highlight Box */}
          <div className="bg-gradient-to-br from-[#00E676]/20 to-[#00E676]/5 border-2 border-[#00E676]/40 rounded-xl p-8 mb-8 text-center">
            <p className="text-white/60 text-sm mb-3 uppercase tracking-wider">The Formula</p>
            <div className="text-2xl md:text-3xl font-bold mb-4">
              <span className="text-[#00E676]">Win Rate</span>
              <span className="text-white/40 mx-3">=</span>
              <span className="text-white">
                <span className="text-green-400">Winning Trades</span>
                <span className="text-white/40 mx-2">÷</span>
                <span className="text-blue-400">Total Trades</span>
                <span className="text-white/40 mx-2">×</span>
                <span className="text-purple-400">100</span>
              </span>
            </div>
            <p className="text-white/60 text-sm">
              Expressed as a percentage
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Win rate in trading</strong> is simply the percentage 
              of your trades that end profitably. If you take 100 trades and 55 are winners, your 
              win rate is 55%. That's it—nothing more, nothing less.
            </p>
          </div>
          
          {/* Example Box */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Calculator size={18} className="text-[#00E676]" />
              Simple Example
            </h4>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-white/50 text-sm mb-2">Your trading month:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-white/70">Total Trades:</span>
                    <span className="text-blue-400 font-medium">40</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/70">Winning Trades:</span>
                    <span className="text-green-400 font-medium">28</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/70">Losing Trades:</span>
                    <span className="text-red-400 font-medium">12</span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#00E676]/10 rounded-lg p-4">
                <p className="text-white/50 text-sm mb-2">Calculation:</p>
                <p className="text-lg font-mono">
                  28 ÷ 40 × 100 = <span className="text-[#00E676] font-bold">70%</span>
                </p>
                <p className="text-white/60 text-xs mt-2">
                  Your win rate is 70%
                </p>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Sounds good, right? But here's the question that matters: <strong className="text-white">are 
              you actually making money?</strong> Win rate alone cannot answer this.
            </p>
          </div>
        </section>

        {/* Section 2: Why Win Rate Is So Popular */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">02</span>
            Why Win Rate Is So Popular
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Before we discuss its limitations, let's understand why win rate dominates trading 
              conversations. There are real reasons it's popular—even if those reasons don't make 
              it the best metric.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "Easy to Understand",
                description: "70% wins means 70 out of 100 trades make money. No complex math required. Anyone can calculate and compare it.",
                icon: Calculator
              },
              {
                title: "Psychologically Comforting",
                description: "Humans hate losing. A high win rate feels safer. 'I win more often than I lose' provides emotional comfort.",
                icon: Heart
              },
              {
                title: "Illusion of Control",
                description: "Higher win rate suggests mastery. It implies you 'know what you're doing.' This feeds the ego—even when results say otherwise.",
                icon: Trophy
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2 text-sm">{item.title}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              These reasons are understandable. But they're also why win rate can be a trap. 
              The metric that's easiest to obsess over isn't necessarily the one that matters most.
            </p>
          </div>
        </section>

        {/* Section 3: Why Win Rate Alone Is Misleading */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">03</span>
            Why Win Rate Alone Is Misleading
          </h2>
          
          <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">The fundamental problem:</strong> win rate measures 
              frequency of winning, not magnitude. It tells you how often you win, not how much 
              you win. And in trading, how much matters more than how often.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                issue: "Ignores Risk/Reward",
                description: "Win rate says nothing about the size of wins versus losses. A 70% win rate with tiny winners and large losers is a losing strategy.",
                icon: Scales
              },
              {
                issue: "Ignores Position Sizing",
                description: "If you win 80% but your losses are 5x your wins, you're losing money. Win rate doesn't capture this relationship.",
                icon: ChartBar
              },
              {
                issue: "Can Hide Bad Strategies",
                description: "Strategies that pick up small profits frequently while occasionally suffering massive losses look great on win rate—until the big loss hits.",
                icon: Warning
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.issue}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This is why understanding metrics like <Link to="/profit-factor-trading" className="text-[#00E676] hover:underline">profit factor</Link> gives 
              a much clearer picture of actual performance. Profit factor captures the relationship 
              between total gains and total losses—what win rate misses entirely.
            </p>
          </div>
        </section>

        {/* Section 4: High vs Low Win Rate Strategies */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">04</span>
            High Win Rate vs Low Win Rate Strategies
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Let's make this concrete with two traders. Same number of trades. Very different 
              win rates. Which one is more profitable?
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Smiley size={24} weight="fill" className="text-green-400" />
                  <h4 className="font-semibold text-green-400">Trader A: High Win Rate</h4>
                </div>
                <div className="space-y-2 text-sm text-white/60 mb-4">
                  <p>Total trades: <span className="text-white">100</span></p>
                  <p>Win rate: <span className="text-green-400 font-semibold">80%</span> (80 wins)</p>
                  <p>Average win: <span className="text-green-400">+$50</span></p>
                  <p>Average loss: <span className="text-red-400">-$250</span></p>
                </div>
                <div className="bg-red-500/10 rounded p-3">
                  <p className="text-xs text-white/50 mb-1">Result:</p>
                  <p className="text-sm">Profit: $4,000 | Loss: $5,000</p>
                  <p className="text-sm font-bold text-red-400">Net: -$1,000 (LOSING)</p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <SmileyMeh size={24} weight="fill" className="text-yellow-400" />
                  <h4 className="font-semibold text-yellow-400">Trader B: Low Win Rate</h4>
                </div>
                <div className="space-y-2 text-sm text-white/60 mb-4">
                  <p>Total trades: <span className="text-white">100</span></p>
                  <p>Win rate: <span className="text-yellow-400 font-semibold">35%</span> (35 wins)</p>
                  <p>Average win: <span className="text-green-400">+$400</span></p>
                  <p>Average loss: <span className="text-red-400">-$100</span></p>
                </div>
                <div className="bg-green-500/10 rounded p-3">
                  <p className="text-xs text-white/50 mb-1">Result:</p>
                  <p className="text-sm">Profit: $14,000 | Loss: $6,500</p>
                  <p className="text-sm font-bold text-green-400">Net: +$7,500 (WINNING)</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <p className="text-white/80 text-sm leading-relaxed">
              <strong className="text-white">The lesson:</strong> Trader A "wins" twice as often 
              but loses money. Trader B "loses" twice as often but is highly profitable. If you 
              only looked at win rate, you'd conclude Trader A is better. The reality is the 
              opposite. This is why <strong className="text-white">win rate vs risk reward</strong> analysis 
              matters so much.
            </p>
          </div>
        </section>

        {/* Section 5: The Relationship Between Win Rate and Risk Reward */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            The Relationship Between Win Rate and Risk Reward
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Win rate and <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline">risk/reward ratio</Link> work 
              together in a mathematical relationship. You can be profitable at any win rate—if 
              the risk/reward compensates. Here's how they connect:
            </p>
          </div>
          
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Win Rate</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Minimum R:R to Break Even</th>
                  <th className="text-left py-3 px-4 text-white/60 font-medium">Strategy Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  { winRate: "25%", rr: "1:3", type: "Trend following, big moves" },
                  { winRate: "33%", rr: "1:2", type: "Swing trading" },
                  { winRate: "40%", rr: "1:1.5", type: "Day trading" },
                  { winRate: "50%", rr: "1:1", type: "Balanced approach" },
                  { winRate: "60%", rr: "1:0.67", type: "Scalping, quick profits" },
                  { winRate: "75%", rr: "1:0.33", type: "High probability, small gains" }
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4 font-medium text-[#00E676]">{row.winRate}</td>
                    <td className="py-3 px-4">{row.rr}</td>
                    <td className="py-3 px-4 text-white/60">{row.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">The tradeoff is real:</strong> Higher win rate 
              strategies typically have smaller winners relative to losers. Lower win rate 
              strategies need bigger winners to compensate. Neither is inherently better—what 
              matters is whether the combination produces positive expectancy.
            </p>
          </div>
        </section>

        {/* Section 6: The Psychological Trap */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-purple-400 text-lg font-mono">06</span>
            The Psychological Trap
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              The obsession with win rate isn't just an analytical error—it's psychological. 
              Our brains are wired to prefer frequent small rewards over infrequent large ones, 
              even when the math favors the latter.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                trap: "Chasing High Win Rate",
                description: "Traders close profitable trades too early to 'lock in' wins, boosting win rate while destroying average win size.",
                icon: TrendUp
              },
              {
                trap: "Fear of Losing",
                description: "Moving stops to avoid losses, holding losers hoping they'll recover—all to protect win rate at the cost of average loss size.",
                icon: Heartbeat
              },
              {
                trap: "Closing Winners Early",
                description: "The anxiety of watching open profit triggers premature exits. You feel good about winning, but your winners never reach their potential.",
                icon: XCircle
              },
              {
                trap: "Avoiding Valid Setups",
                description: "Skipping trades that might lose to protect your win rate, even when the expected value is positive.",
                icon: Target
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-purple-500/20 rounded-lg p-5"
              >
                <item.icon size={20} weight="duotone" className="text-purple-400 mb-2" />
                <h4 className="font-semibold mb-2 text-sm">{item.trap}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <BookOpen size={24} weight="duotone" className="text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">On Probability Thinking</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  In <strong className="text-white">"Trading in the Zone,"</strong> Mark Douglas 
                  explains that consistent traders think in probabilities, not individual outcomes. 
                  They accept that any single trade is uncertain—what matters is the edge playing 
                  out over many trades. Obsessing over win rate keeps you focused on individual 
                  outcomes, which is exactly the wrong mindset for long-term profitability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Why Traders Misinterpret Their Performance */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">07</span>
            Why Traders Misinterpret Their Performance
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Even traders who intellectually understand win rate's limitations often misinterpret 
              their own results. The errors are predictable:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                mistake: "Focusing on Percentage Instead of Profitability",
                description: "Celebrating 65% win rate while ignoring that overall P&L is negative. The number that matters—actual profit—gets less attention than the number that feels good.",
                icon: Percent
              },
              {
                mistake: "Ignoring Data Context",
                description: "A 70% win rate during a strong bull market might be 40% in ranging conditions. Context determines whether the win rate is skill or tailwind.",
                icon: ChartLineUp
              },
              {
                mistake: "Small Sample Size Issues",
                description: "Winning 8 out of 10 trades feels like 80% win rate. But 10 trades is statistically meaningless—you can't draw conclusions from such a small sample.",
                icon: ChartBar
              },
              {
                mistake: "Survivorship Bias",
                description: "Remembering winning streaks, forgetting losing ones. Memory distorts your actual win rate, usually in a flattering direction.",
                icon: Brain
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-orange-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.mistake}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This is why tracking matters. You need enough trades—typically 100+ minimum—to 
              draw meaningful conclusions. Learn more about <Link to="/how-many-trades-to-validate-a-strategy" className="text-[#00E676] hover:underline">how many trades you need to validate a strategy</Link>.
            </p>
          </div>
        </section>

        {/* Section 8: What Actually Matters More Than Win Rate */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            What Actually Matters More Than Win Rate
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              If win rate isn't the key metric, what is? Here are the numbers professional 
              traders actually focus on:
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                metric: "Profit Factor",
                description: "Total profits ÷ Total losses. A single number capturing the efficiency of your entire strategy. Above 1.5 is solid; above 2.0 is excellent.",
                icon: Calculator,
                link: "/profit-factor-trading"
              },
              {
                metric: "Expectancy",
                description: "Average profit per trade. Positive expectancy means your strategy makes money over time, regardless of win rate.",
                icon: TrendUp,
                link: null
              },
              {
                metric: "Execution Quality",
                description: "Did you follow your rules? A perfect strategy executed poorly will underperform. Consistency of execution matters more than individual outcomes.",
                icon: Crosshair,
                link: null
              },
              {
                metric: "Long-Term Consistency",
                description: "Are results stable month to month? High variance suggests luck or changing conditions. Consistency suggests repeatable edge.",
                icon: ArrowsClockwise,
                link: null
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2">{item.metric}</h4>
                <p className="text-white/60 text-sm leading-relaxed mb-2">{item.description}</p>
                {item.link && (
                  <Link to={item.link} className="text-[#00E676] text-xs hover:underline">
                    Learn more →
                  </Link>
                )}
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Win rate is one piece of the puzzle. But it's the piece most traders over-weight 
              while ignoring the metrics that actually determine success.
            </p>
          </div>
        </section>

        {/* Section 9: How to Use Win Rate Correctly */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            How to Use Win Rate Correctly
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Win rate isn't useless—it just shouldn't be used alone. Here's how to incorporate 
              it properly into your analysis:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Lightbulb size={20} weight="duotone" className="text-[#00E676]" />
              Using Win Rate as Part of a System
            </h3>
            <div className="space-y-4">
              {[
                {
                  point: "Combine with Risk/Reward",
                  detail: "Always pair win rate with average R:R achieved. Together they tell the real story."
                },
                {
                  point: "Segment by Setup Type",
                  detail: "Your breakout trades might have 45% win rate, reversals 60%. Aggregate hides this insight."
                },
                {
                  point: "Track Over Sufficient Sample",
                  detail: "Win rate over 20 trades is noise. Win rate over 200 trades is signal."
                },
                {
                  point: "Consider Market Conditions",
                  detail: "Your win rate will vary by market environment. Track separately to understand your true edge."
                }
              ].map((item, index) => (
                <div key={index} className="border-l-2 border-[#00E676]/50 pl-4">
                  <h4 className="font-medium text-sm mb-1">{item.point}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Many traders use tools like <strong className="text-white">GENI</strong> to analyze 
              win rate alongside other metrics like profit factor and risk/reward, giving a more 
              complete view of performance. But regardless of tools, the principle is the same: 
              never evaluate win rate in isolation.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Understanding how <strong className="text-white">win rate</strong> fits into your 
              overall performance is key to becoming a more consistent trader. It's not about 
              ignoring win rate—it's about putting it in proper context alongside the metrics 
              that actually determine profitability.
            </p>
            <p className="text-white/70 leading-relaxed">
              The best traders don't chase high win rates. They chase positive expectancy, 
              consistent execution, and sustainable edge. Win rate is one input among many—and 
              not the most important one.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/profit-factor-trading" className="text-[#00E676] hover:underline text-sm">
                What Is Profit Factor →
              </Link>
              <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline text-sm">
                Improve Risk Reward Ratio →
              </Link>
              <Link to="/how-many-trades-to-validate-a-strategy" className="text-[#00E676] hover:underline text-sm">
                How Many Trades to Validate →
              </Link>
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/profit-factor-trading" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">What Is Profit Factor</h3>
              <p className="text-white/50 text-sm">A better metric for evaluating strategy quality.</p>
            </Link>
            <Link to="/improve-risk-reward-ratio" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Improve Risk Reward Ratio</h3>
              <p className="text-white/50 text-sm">Better R:R through execution and psychology.</p>
            </Link>
            <Link to="/how-many-trades-to-validate-a-strategy" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Validate Your Strategy</h3>
              <p className="text-white/50 text-sm">How many trades you need for reliable data.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Percent size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              See the Full Picture
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Stop focusing on win rate alone. Track all your metrics together to understand 
              what's actually driving your results—and what isn't.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "What Is Win Rate in Trading (And Why It Can Be Misleading)",
              "description": "Learn what win rate is in trading and why it can be misleading if not combined with other performance metrics.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/win-rate-trading"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WinRateTrading;
